import { createClaim } from "@/api/receiver/createClaim";
import { createClaimDetail } from "@/api/receiver/createClaimDetail";
import { Claim } from "@/api/receiver/getClaims";
import { resolveClaim } from "@/api/receiver/resolveClaim";
import { sonner } from "@/components/ui/useSonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useResolveClaim = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (claimId: string) => resolveClaim({ claimId }),
    onMutate: async (claimId: string) => {
      // Store the previous claims data
      const previousClaims = queryClient.getQueryData<Claim[]>(["claims"]);

      // Optimistically update the claims data
      if (previousClaims) {
        const updatedClaims = previousClaims.map((claim: Claim) => {
          if (claim.id === claimId) {
            return { ...claim, state: "closed" };
          }
          return claim;
        });

        queryClient.setQueryData(["claims"], updatedClaims);
      }

      // Pass previousClaims as context for rollback
      return { preData: previousClaims };
    },
    onSuccess: () => {
      // invalidate queries
      queryClient.invalidateQueries({ queryKey: ["claims"] });
      // Show success notification
      sonner().success("Claim resolved successfully", {
        duration: 3000,
      });
    },
    onError: (error, variables, context) => {
      // Rollback to previous claims data
      if (context?.preData) {
        queryClient.setQueryData(["claims"], context.preData);
      }
      // Show error notification
      sonner().error(`Failed to resolve claim. Please try again.`, {
        duration: 5000,
      });
    },
  });
};

export const useCreateClaimDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      claimId,
      message,
      tempId,
    }: {
      claimId: string;
      message: string;
      tempId?: string;
    }) =>
      createClaimDetail({
        claimId,
        message,
        tempId,
      }),
    // Optimistically update the claims data
    onMutate: async ({ claimId, message }) => {
      // Store the previous claims data
      const previousClaims = queryClient.getQueryData<Claim[]>(["claims"]);

      // Optimistically update the claims data
      if (previousClaims) {
        const updatedClaims = previousClaims.map((claim: Claim) => {
          if (claim.id === claimId) {
            return {
              ...claim,
              detailsHistory: [
                ...claim.detailsHistory,
                { message, createdAt: new Date() },
              ],
            };
          }
          return claim;
        });
        queryClient.setQueryData(["claims"], updatedClaims);
      }
      return { preData: previousClaims };
    },
    // Rollback to previous claims data on error
    onError: (error, variables, context) => {
      // Rollback to previous claims data
      if (context?.preData) {
        queryClient.setQueryData(["claims"], context.preData);
      }
      // Show error notification
      sonner().error(`Failed to add claim detail. Please try again.`, {
        duration: 5000,
      });
    },
    onSuccess: () => {
      // invalidate queries
      queryClient.invalidateQueries({ queryKey: ["claims"] });
      // Show success notification
      sonner().success("Claim detail added successfully", {
        duration: 3000,
      });
    },
  });
};

export const useAddClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submittedTo,
      loadId,
      claimAmount,
      message,
      tempId,
    }: {
      submittedTo?: string | null;
      loadId: string;
      claimAmount: number;
      message: string;
      tempId?: string;
    }) =>
      createClaim({
        submittedTo,
        loadId,
        claimAmount,
        message,
        tempId,
      }),
    onSuccess: () => {
      // invalidate queries
      queryClient.invalidateQueries({ queryKey: ["claims"] });
      // Show success notification
      sonner().success("Claim added successfully.", {
        duration: 3000,
      });
    },
    onError: (error: any) => {
      // Show error notification
      sonner().error(`Failed to add claim. Please try again.`, {
        duration: 5000,
      });
    },
  });
};
